import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { Call } from '../types';

interface CallHistoryTableProps {
  calls: Call[];
  onViewCall: (id: string) => void;
  onBlockNumber?: (callId: string, callerNumber: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export const CallHistoryTable: React.FC<CallHistoryTableProps> = ({
  calls,
  onViewCall,
  onBlockNumber,
  page,
  pageSize,
  totalCount,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'initiated':
        return (
          <Badge variant="info" size="sm">
            {t('calls.initiated')}
          </Badge>
        );
      case 'ringing':
        return (
          <Badge variant="warning" size="sm">
            {t('calls.ringing')}
          </Badge>
        );
      case 'answered':
        return (
          <Badge variant="success" size="sm">
            {t('calls.answered')}
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge variant="success" size="sm">
            {t('calls.inProgress')}
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="secondary" size="sm">
            {t('calls.completed')}
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" size="sm">
            {t('calls.failed')}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="secondary" size="sm">
            {t('calls.cancelled')}
          </Badge>
        );
      case 'busy':
        return (
          <Badge variant="warning" size="sm">
            {t('calls.busy')}
          </Badge>
        );
      case 'no_answer':
        return (
          <Badge variant="secondary" size="sm">
            {t('calls.noAnswer')}
          </Badge>
        );
      case 'transferred':
        return (
          <Badge variant="info" size="sm">
            {t('calls.transferred')}
          </Badge>
        );
      case 'escalated':
        return (
          <Badge variant="warning" size="sm">
            {t('calls.escalated')}
          </Badge>
        );
      case 'blocked':
        return (
          <Badge variant="error" size="sm">
            {t('blockedNumbers.blocked')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {status}
          </Badge>
        );
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.callId')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.agent')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.caller')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.calledNumber')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.duration')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.status')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.date')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">
                  {t('calls.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {calls.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          history
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('calls.noCallHistoryFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('calls.callHistoryWillAppearHere')}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                calls.map(call => (
                  <tr
                    key={call.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          #{call.id}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {call.direction_display}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {call.agent?.name || 'No Agent'}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {call.agent?.voice?.name || 'No Voice'}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {call.caller_number}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(call.started_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {call.called_number}
                    </td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white">
                      {Math.floor(call.duration / 60)}:
                      {(call.duration % 60).toString().padStart(2, '0')}
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(call.status)}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                      {new Date(call.started_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewCall(call.id)}
                        className="p-1 h-8 w-8"
                        title="View Call"
                      >
                        <span className="material-icons text-lg">visibility</span>
                      </Button>
                      {onBlockNumber && call.caller_number && call.status !== 'blocked' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onBlockNumber(call.id, call.caller_number)}
                          className="p-1 h-8 w-8 text-orange-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title={t('blockedNumbers.blockThisNumber')}
                        >
                          <span className="material-icons text-lg">block</span>
                        </Button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                title="Previous"
                className="h-8 w-8 p-0"
              >
                <span className="material-icons text-base">chevron_left</span>
              </Button>

              {(() => {
                const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
                const pages: (number | 'ellipsis')[] = [];
                const add = (n: number) => {
                  if (!pages.includes(n)) pages.push(n);
                };
                add(1);
                add(totalPages);
                add(page);
                add(page - 1);
                add(page + 1);
                const normalized = Array.from(
                  new Set(
                    pages
                      .filter(p => typeof p === 'number' && p >= 1 && (p as number) <= totalPages)
                      .sort((a, b) => (a as number) - (b as number))
                  )
                );
                const withEllipsis: (number | 'ellipsis')[] = [];
                for (let i = 0; i < normalized.length; i++) {
                  const current = normalized[i] as number;
                  const prev = normalized[i - 1] as number | undefined;
                  if (prev !== undefined && current - prev > 1) {
                    withEllipsis.push('ellipsis');
                  }
                  withEllipsis.push(current);
                }
                return withEllipsis.map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span
                      key={`e-${idx}`}
                      className="px-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      …
                    </span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === page ? 'light' : 'gradient-multi'}
                      size="sm"
                      className="h-8 px-3"
                      onClick={() => onPageChange(p as number)}
                    >
                      {p}
                    </Button>
                  )
                );
              })()}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(page * pageSize < totalCount ? page + 1 : page)}
                disabled={page * pageSize >= totalCount}
                title="Next"
                className="h-8 w-8 p-0"
              >
                <span className="material-icons text-base">chevron_right</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
